export type LandscapeLockResult = 'not-needed' | 'locked' | 'unsupported' | 'failed';

type LockableOrientation = ScreenOrientation & {
  lock?: (orientation: 'landscape') => Promise<void>;
};

type WebkitFullscreenDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
  webkitFullscreenEnabled?: boolean;
};

type WebkitFullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

export function fullscreenElement(documentValue: Document = document): Element | null {
  const webkitDocument = documentValue as WebkitFullscreenDocument;
  return documentValue.fullscreenElement ?? webkitDocument.webkitFullscreenElement ?? null;
}

export function fullscreenAvailable(
  element: HTMLElement,
  documentValue: Document = document,
): boolean {
  const webkitDocument = documentValue as WebkitFullscreenDocument;
  const webkitElement = element as WebkitFullscreenElement;
  return Boolean(
    documentValue.fullscreenEnabled ||
    webkitDocument.webkitFullscreenEnabled ||
    webkitElement.webkitRequestFullscreen,
  );
}

export async function enterFullscreen(element: HTMLElement): Promise<void> {
  if (element.requestFullscreen) {
    await element.requestFullscreen();
    return;
  }
  const webkitRequestFullscreen = (element as WebkitFullscreenElement).webkitRequestFullscreen;
  if (!webkitRequestFullscreen) throw new Error('FULLSCREEN_UNSUPPORTED');
  await webkitRequestFullscreen.call(element);
}

export async function leaveFullscreen(documentValue: Document = document): Promise<void> {
  if (documentValue.exitFullscreen) {
    await documentValue.exitFullscreen();
    return;
  }
  const webkitExitFullscreen = (documentValue as WebkitFullscreenDocument).webkitExitFullscreen;
  if (!webkitExitFullscreen) throw new Error('FULLSCREEN_UNSUPPORTED');
  await webkitExitFullscreen.call(documentValue);
}

export function isTouchDevice(): boolean {
  return navigator.maxTouchPoints > 0 || window.matchMedia('(pointer: coarse)').matches;
}

export function isPortraitViewport(): boolean {
  return window.matchMedia('(orientation: portrait)').matches;
}

export async function lockLandscapeForTouchDevice(): Promise<LandscapeLockResult> {
  if (!isTouchDevice()) return 'not-needed';
  const orientation = screen.orientation as LockableOrientation | undefined;
  if (!orientation?.lock) return 'unsupported';
  try {
    await orientation.lock('landscape');
    return 'locked';
  } catch {
    return 'failed';
  }
}

export function unlockOrientation(): void {
  try {
    screen.orientation?.unlock();
  } catch {
    // Bazı tarayıcılar tam ekrandan çıktıktan sonra unlock çağrısını reddedebilir.
  }
}
